import { noop } from 'lodash/fp';
import { FC, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import NavBack from 'dashboard/components/NavBack';
import WorkflowControlPanel from 'dashboard/components/WorkflowControlPanel';
import WorkflowActionMetaProvider from 'dashboard/components/WorkflowEditor/WorkflowActionMetaProvider';
import useModuleToBlockStepsConfigQuery from 'dashboard/components/WorkflowEditor/WorkflowCanvas/useModuleToBlockStepsConfigQuery';
import WorkflowCanvas from 'dashboard/components/WorkflowEditor/WorkflowCanvas/WorkflowCanvas';
import { WorkflowEditorContainer } from 'dashboard/components/WorkflowEditor/WorkflowEditor';
import { FlowPlotterController } from 'dashboard/components-lib/FlowPlotter';
import { FullScreenHeaderContainer } from 'dashboard/layouts/ShowViewLayoutV2';
import { getLoopTokensToCycleIdx, getTokensToRunSteps } from 'dashboard/lib/workflows';
import { Workflow, WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowRun } from 'dashboard/models/workflow-run';
import { WorkflowRunStep, WorkflowRunStepKnownObjects } from 'dashboard/models/workflow-step';
import { WorkflowStepInput, WorkflowVersion } from 'dashboard/models/workflow-version';
import { useListExternalIntegrationVendorsQuery } from 'dashboard/redux/api/external-integration-vendor';
import { WorkflowRunState } from 'dashboard/redux/reducers/workflow-run';
import { useUsableObjects } from 'dashboard/redux/selectors/workflow-editor-context';
import { WorkflowEditorState } from 'dashboard/redux/slices/workflow-editor';
import useWorkflowStepSelectionState from 'dashboard/views/DashboardWorkflowRunsShowView/useWorkflowStepSelectionState';
import { getWorkflowRunStepErrors } from 'dashboard/views/DashboardWorkflowRunsShowView/utils';
import WorkflowDiagnosticsPanel from 'dashboard/views/DashboardWorkflowRunsShowView/WorkflowDiagnosticsPanel';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import ToggleSlider from 'design-system/components/ToggleSlider';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import useToggle from 'lib/hooks/useToggle';
import { buildGraph } from 'lib/workflows';

// N.B. this is just to match the editors height
const StyledFullScreenHeaderContainer = styled(FullScreenHeaderContainer)`
  height: 64px;
`;

const CenterPanel = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  overflow-x: hidden;
`;

// The height: 0 is needed to allow this div to compress to height less than the minimum canvas zoom area.
const CanvasContainer = styled.div`
  flex-grow: 1;
  height: 0;
`;

interface DashboardWorkflowRunsShowViewProps {
  editorRoute: { label: string; path: string } | null;
  backRoute: string;
  helmetTitleSuffix: string;
  inputStep: WorkflowStepInput;
  knownObjects: WorkflowRunStepKnownObjects[];
  steps: WorkflowRunStep[];
  loopChildrenSteps: Record<string, Record<number, WorkflowRunStep[]>>;
  moduleRunStepsByConfigStepToken: WorkflowEditorState['moduleRunStepsByConfigStepToken'];
  triggerObject: WorkflowRunState['triggerObject'];
  workflow: Workflow;
  workflowRun: WorkflowRun;
  workflowSchema: WorkflowSchema;
  workflowVersion: WorkflowVersion;
  isLoadingLoopCycle: boolean;
  handleLoadLoopChildrenSteps: (
    workflowRunId: string,
    loopStepId: string,
    loopIterationIdx: number,
  ) => Promise<void>;
}

const DashboardWorkflowRunsShowView: FC<DashboardWorkflowRunsShowViewProps> = ({
  editorRoute,
  backRoute,
  helmetTitleSuffix,
  inputStep,
  knownObjects,
  steps,
  loopChildrenSteps,
  moduleRunStepsByConfigStepToken,
  triggerObject,
  workflow,
  workflowRun,
  workflowSchema,
  workflowVersion,
  isLoadingLoopCycle,
  handleLoadLoopChildrenSteps,
}) => {
  const isPersonerd = useIsPersonerd();
  const [loopTokensToCycleIdx, setLoopTokensToCycleIdx] = useState<Record<string, number>>({});
  const stepsConfig = workflowVersion.attributes['steps-config'];
  const tokensToSteps = useMemo(() => buildGraph(stepsConfig), [stepsConfig]);
  const stepsWithSelectedLoopCycles = useMemo<WorkflowRunStep[]>(() => {
    return Object.entries(loopTokensToCycleIdx).reduce<WorkflowRunStep[]>(
      (stepsWithSelectedLoopCycles, [token, idx]) => [
        ...stepsWithSelectedLoopCycles,
        ...(loopChildrenSteps[token]?.[idx] ?? []),
      ],
      steps,
    );
  }, [loopTokensToCycleIdx, steps, loopChildrenSteps]);
  const stepsWithAllLoopCycles = useMemo<WorkflowRunStep[]>(
    () => [
      ...steps,
      ...Object.values(loopChildrenSteps)
        .map((s) => Object.values(s))
        .flat(2),
    ],
    [steps, loopChildrenSteps],
  );
  const tokensToRunSteps = useMemo(
    () => getTokensToRunSteps(loopTokensToCycleIdx, tokensToSteps, stepsWithSelectedLoopCycles),
    [stepsWithSelectedLoopCycles, loopTokensToCycleIdx, tokensToSteps],
  );
  const moduleBlockSteps = useModuleToBlockStepsConfigQuery(stepsConfig);
  const [
    { activeStep, activeRunStep, selectedToken, focusedToken, moduleToken },
    { selectStep, deselectStep, focusStep },
  ] = useWorkflowStepSelectionState({
    inputStep,
    moduleBlockSteps,
    moduleRunStepsByConfigStepToken,
    tokensToRunSteps,
    tokensToSteps,
  });
  const { data: externalIntegrationVendors = [] } = useListExternalIntegrationVendorsQuery();
  const updateCycleIndex = async (idx: number, token: string) => {
    setLoopTokensToCycleIdx({ ...loopTokensToCycleIdx, [token]: idx });
    if (!loopChildrenSteps[token] || !loopChildrenSteps[token][idx]) {
      await handleLoadLoopChildrenSteps(workflowRun.id, token, idx);
    }
  };

  useEffect(() => {
    setLoopTokensToCycleIdx(getLoopTokensToCycleIdx(steps));
  }, [steps]);

  const runStepErrors = useMemo(
    () => getWorkflowRunStepErrors(stepsWithSelectedLoopCycles),
    [stepsWithSelectedLoopCycles],
  );
  const [showRunStats, { toggle: toggleShowRunStats, disable: stopShowRunStats }] =
    useToggle(false);

  const usableObjects = useUsableObjects(
    selectedToken && tokensToSteps[selectedToken] ? selectedToken : undefined,
  );

  return (
    <>
      <Helmet
        title={`${workflowRun.id} - Runs - ${workflow.attributes.name} - ${helmetTitleSuffix}`}
      />
      <WorkflowActionMetaProvider
        context='workflow-runs'
        externalIntegrationVendors={externalIntegrationVendors}
      >
        <StyledFullScreenHeaderContainer>
          <NavBack to={backRoute}>{workflow.attributes.name}</NavBack>
          <HStack>
            {editorRoute != null && (
              <Link to={editorRoute.path}>
                <Button
                  id='dashboard-workflow-runs-show-view__open-editor'
                  variant='basic'
                  type='button'
                >
                  {editorRoute.label}
                </Button>
              </Link>
            )}
            {isPersonerd && (
              <ToggleSlider
                titleOn='show run stats'
                titleOff='hide run stats'
                labelOn='Run stats'
                value={showRunStats}
                onChange={toggleShowRunStats}
              />
            )}
          </HStack>
        </StyledFullScreenHeaderContainer>
        <WorkflowEditorContainer isShowingHeaderStatus={false}>
          <CenterPanel>
            <CanvasContainer>
              <FlowPlotterController>
                <WorkflowCanvas
                  editable={false}
                  showRunStats={showRunStats}
                  focusedNodeToken={focusedToken}
                  selectedNodeToken={selectedToken}
                  selectedModuleToken={moduleToken}
                  erroredNodeTokens={[]}
                  runLoopTokensToFocusedCycle={loopTokensToCycleIdx}
                  runSteps={stepsWithSelectedLoopCycles}
                  moduleRunSteps={moduleRunStepsByConfigStepToken}
                  steps={stepsConfig}
                  inputStep={inputStep}
                  addNodeAfter={noop}
                  addNodeAfterBranchStart={noop}
                  moveStep={noop}
                  selectNode={selectStep}
                />
              </FlowPlotterController>
            </CanvasContainer>
            {showRunStats && (
              <WorkflowDiagnosticsPanel
                workflowVersion={workflowVersion}
                workflowRun={workflowRun}
                tokensToSteps={tokensToSteps}
                tokensToRunSteps={tokensToRunSteps}
                allRunSteps={stepsWithAllLoopCycles}
                activeNode={activeStep}
                selectNode={selectStep}
                onExit={stopShowRunStats}
                updateLoopCycle={(loopCycle) => {
                  setLoopTokensToCycleIdx({ ...loopTokensToCycleIdx, ...loopCycle });
                }}
              />
            )}
          </CenterPanel>
          {activeStep && (
            <WorkflowControlPanel
              usableObjects={usableObjects}
              activeNode={activeStep}
              conditionalRouteToEdit={undefined}
              editable={false}
              knownObjects={knownObjects}
              loopTokensToCycleIdx={loopTokensToCycleIdx}
              stepsConfig={stepsConfig}
              tokensToSteps={tokensToSteps}
              inputStep={inputStep}
              triggerObject={triggerObject}
              validationErrors={runStepErrors}
              workflowId={workflow.id}
              workflowName={workflow.attributes.name}
              workflowRun={workflowRun}
              workflowRunStep={activeRunStep}
              workflowSchema={workflowSchema}
              platformObjectJsonSchema={undefined}
              workflowVersionOutput={workflowVersion.attributes.outputs}
              workflowVersionExpiration={workflowVersion.attributes['time-out-minutes']}
              closePanel={deselectStep}
              deleteStep={noop}
              setConditionalRouteToEdit={noop}
              setFocusedNodeToken={focusStep}
              isLoadingLoopCycle={isLoadingLoopCycle}
              updateCycleIndex={updateCycleIndex}
              updateStep={noop}
              updateInputStep={noop}
              updateWorkflowVersionOutput={noop}
              showRunStats={showRunStats}
            />
          )}
        </WorkflowEditorContainer>
      </WorkflowActionMetaProvider>
    </>
  );
};

export default DashboardWorkflowRunsShowView;
